const chapter = {
  id: 'p-block-13',
  slug: 'p-block-13',
  subject: 'chemistry',
  subjectLabel: 'Chemistry',
  exam: 'JEE Main + NEET',
  title: {
    english: 'p-Block — Group 13 (Boron Family)',
        hindi: 'p-खण्ड — वर्ग 13 (बोरॉन परिवार)',
  },
  estReadMinutes: 55,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '1–2 questions in JEE Main / NEET',
        hindi: 'जेईई मेन/नीट में 1-2 प्रश्न',
  },
  syllabusTags: ['Inorganic Chemistry', 'JEE-Main', 'NEET', 'Class-11'],
  sections: [
    {
      id: 'general',
      title: {
        english: 'General Trends in Group 13 (B, Al, Ga, In, Tl)',
        hindi: 'समूह 13 में सामान्य रुझान (बी, अल, गा, इन, टीएल)',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Outer config $ns^2 np^1$. Common oxidation states +3 and +1.',
        hindi: 'बाहरी कॉन्फ़िगरेशन $ns^2 np^1$। सामान्य ऑक्सीकरण अवस्थाएँ +3 और +1 हैं। **B एक अधातु (धातु) है**; शेष धातु हैं। बी सहसंयोजक नेटवर्क ठोस के रूप में मौजूद है; बहुत ऊँचा एम.पी.',
            },
            {
              english: '**B is a non-metal (metalloid)**; rest are metals. B exists as covalent network solid; very high m.p.',
        hindi: '**बी एक अधातु (धातु) है**; शेष धातु हैं। बी सहसंयोजक नेटवर्क ठोस के रूप में मौजूद है; बहुत ऊँचा एम.पी.',
            },
            {
              english: 'Atomic radius: B < Al > Ga (Ga smaller than Al because of poor shielding by intervening $3d^{10}$ electrons).',
        hindi: 'परमाणु त्रिज्या: B < Al > Ga ($3d^{10}$ इलेक्ट्रॉनों के हस्तक्षेप से खराब परिरक्षण के कारण Ga, Al से छोटा है)। **अक्रिय-जोड़ी प्रभाव**: $3d^{10}$ जोड़ी की गैर-बंधित रहने की प्रवृत्ति। +1 अवस्था की स्थिरता समूह में नीचे की ओर बढ़ती है: Tl⁺, Tl³⁺ की तुलना में अधिक स्थिर है।',
            },
            {
              english: '**Inert-pair effect**: tendency of $ns^2$ pair to remain non-bonded. Stability of +1 state increases down the group: Tl⁺ is more stable than Tl³⁺.',
        hindi: '**अक्रिय-युग्म प्रभाव**: $ns^2$ युग्म की अबंधित बने रहने की प्रवृत्ति। +1 अवस्था की स्थिरता समूह में नीचे की ओर बढ़ती है: Tl⁺, Tl³⁺ की तुलना में अधिक स्थिर है।',
            },
            {
              english: 'IE: B > Tl > Ga > Al > In (slightly irregular due to $d$ and $f$ orbital effects).',
        hindi: 'IE: B > Tl > Ga > Al > In ($d$ और $f$ कक्षीय प्रभावों के कारण थोड़ा अनियमित)।',
            },
            {
              english: 'Electronegativity: B (2.0), Al (1.5), Ga (1.6), In (1.7), Tl (1.8) — non-monotonic.',
        hindi: 'इलेक्ट्रोनगेटिविटी: बी (2.0), अल (1.5), गा (1.6), इन (1.7), टीएल (1.8) - गैर-मोनोटोनिक।',
            },
          ],
        },
        {
          type: 'tip',
          text: {
            english: '**Anomalies of Boron** (small size, high IE, no $d$-orbitals, max covalency 4): exclusively covalent, electron-deficient, forms 3-centre 2-electron bonds (in $B_2H_6$), oxide $B_2O_3$ is acidic while $Al_2O_3$ is amphoteric. Resembles Si (diagonal relationship).',
        hindi: '**बोरान की विसंगतियाँ** (छोटा आकार, उच्च IE, कोई $d$-ऑर्बिटल्स नहीं, अधिकतम सहसंयोजकता 4): विशेष रूप से सहसंयोजक, इलेक्ट्रॉन-कमी, 3-केंद्र 2-इलेक्ट्रॉन बांड बनाता है ($B_2H_6$ में), ऑक्साइड $B_2O_3$ अम्लीय है जबकि $Al_2O_3$ उभयधर्मी है। सी (विकर्ण संबंध) जैसा दिखता है।',
          },
        },
      ],
    },
    {
      id: 'reactions',
      title: {
        english: 'Chemical Reactivity',
        hindi: 'रासायनिक क्रियाशीलता',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'With **dioxygen**: $4M + 3O_2 \\to 2M_2O_3$. $B_2O_3$ acidic, $Al_2O_3$ & $Ga_2O_3$ amphoteric, $In_2O_3$ & $Tl_2O_3$ basic.',
        hindi: '**डाइऑक्सीजन** के साथ: $4M + 3O_2 \\to 2M_2O_3$। $B_2O_3$ अम्लीय, $Al_2O_3$ और $Ga_2O_3$ उभयधर्मी, $In_2O_3$ और $Tl_2O_3$ मूल।',
            },
            {
              english: 'With **dinitrogen** (high $T$): $2B + N_2 \\to 2BN$ (boron nitride, "inorganic graphite", isoelectronic with carbon).',
        hindi: '**डाइनिट्रोजन** (उच्च $T$) के साथ: $2B + N_2 \\to 2BN$ (बोरॉन नाइट्राइड, "अकार्बनिक ग्रेफाइट", कार्बन के साथ आइसोइलेक्ट्रॉनिक)।',
            },
            {
              english: 'With **halogens**: $2M + 3X_2 \\to 2MX_3$. All trihalides covalent (except AlF₃, fluorides of heavier members which are ionic). $BX_3$ are Lewis acids; order of acidity: $BF_3 < BCl_3 < BBr_3 < BI_3$ (back-bonding from filled $p$ on X to empty $p$ on B is strongest with F → reduces Lewis acidity).',
        hindi: '**हैलोजन** के साथ: $2M + 3X_2 \\to 2MX_3$। सभी ट्राइहैलाइड सहसंयोजक (AlF₃ को छोड़कर, भारी सदस्यों के फ्लोराइड जो आयनिक होते हैं)। $BX_3$ लुईस एसिड हैं; अम्लता का क्रम: $BF_3 < BCl_3 < BBr_3 < BI_3$ (X पर भरे हुए $p$ से B पर खाली $p$ तक बैक-बॉन्डिंग F के साथ सबसे मजबूत है → लुईस अम्लता को कम करता है)।',
            },
            {
              english: 'With **acids**: $2Al + 6HCl \\to 2AlCl_3 + 3H_2$. Al passivated by conc. HNO₃ (oxide layer).',
        hindi: '**एसिड** के साथ: $2Al + 6HCl \\to 2AlCl_3 + 3H_2$। अल को सांद्रण द्वारा पारित किया गया। HNO₃ (ऑक्साइड परत)।',
            },
            {
              english: 'With **alkali**: $2Al + 2NaOH + 6H_2O \\to 2Na[Al(OH)_4] + 3H_2$ — amphoteric.',
        hindi: '**क्षार** के साथ: $2Al + 2NaOH + 6H_2O \\to 2Na[Al(OH)_4] + 3H_2$ — उभयधर्मी।',
            },
          ],
        },
      ],
    },
    {
      id: 'boron-compounds',
      title: {
        english: 'Important Compounds of Boron',
        hindi: 'बोरॉन के महत्वपूर्ण यौगिक',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Borax** $Na_2B_4O_7\\cdot 10H_2O$ — actually $Na_2[B_4O_5(OH)_4]\\cdot 8H_2O$; aqueous solution alkaline (hydrolysis). On heating: $Na_2B_4O_7\\cdot 10H_2O \\to Na_2B_4O_7 \\to 2NaBO_2 + B_2O_3$ (transparent glassy bead). **Borax-bead test** detects coloured cations (Cu²⁺ blue, Fe³⁺ pale yellow, Cr³⁺ green).',
        hindi: '**बोरेक्स** $Na_2B_4O_7\\cdot 10H_2O$ - वास्तव में $Na_2[B_4O_5(OH)_4]\\cdot 8H_2O$; जलीय घोल क्षारीय (हाइड्रोलिसिस)। गरम करने पर: $Na_2B_4O_7\\cdot 10H_2O \\to Na_2B_4O_7 \\to 2NaBO_2 + B_2O_3$ (पारदर्शी कांचयुक्त मनका)। **बोरेक्स-बीड परीक्षण** रंगीन धनायनों (Cu²⁺ नीला, Fe³⁺ हल्का पीला, Cr³⁺ हरा) का पता लगाता है।',
            },
            {
              english: '**Boric acid** $H_3BO_3$ — soft, white, flaky solid; layered structure with H-bonds. **Monobasic Lewis acid**: not a proton donor itself but accepts OH⁻ from water: $B(OH)_3 + H_2O \\rightleftharpoons [B(OH)_4]^- + H^+$. Heating: $H_3BO_3 \\to HBO_2 \\to B_2O_3$.',
        hindi: '**बोरिक एसिड** $H_3BO_3$ - नरम, सफेद, परतदार ठोस; एच-बॉन्ड के साथ स्तरित संरचना। **मोनोबैसिक लुईस एसिड**: स्वयं प्रोटॉन दाता नहीं है लेकिन पानी से OH⁻ स्वीकार करता है: $B(OH)_3 + H_2O \\rightleftharpoons [B(OH)_4]^- + H^+$। हीटिंग: $H_3BO_3 \\to HBO_2 \\to B_2O_3$.',
            },
            {
              english: '**Diborane** $B_2H_6$ — prepared by $4BF_3 + 3LiAlH_4 \\to 2B_2H_6 + 3LiF + 3AlF_3$. Banana-bond structure: 2 bridging H form **3-centre 2-electron bonds**; 4 terminal B–H bonds are normal 2c-2e. With excess NH₃: $B_2H_6 + 6NH_3 \\to 2[BH_2(NH_3)_2]^+[BH_4]^-$, on heating gives **borazine** $B_3N_3H_6$ ("inorganic benzene", isoelectronic with $C_6H_6$).',
        hindi: '**डिबोरेन** $B_2H_6$ — $4BF_3 + 3LiAlH_4 \\to 2B_2H_6 + 3LiF + 3AlF_3$ द्वारा तैयार किया गया। केले-बंध संरचना: 2 ब्रिजिंग एच फॉर्म **3-केंद्र 2-इलेक्ट्रॉन बांड**; 4 टर्मिनल बी-एच बांड सामान्य 2सी-2ई हैं। अधिक NH₃ के साथ: $B_2H_6 + 6NH_3 \\to 2[BH_2(NH_3)_2]^+[BH_4]^-$, गर्म करने पर **बोराज़ीन** $B_3N_3H_6$ ("अकार्बनिक बेंजीन", $C_6H_6$ के साथ आइसोइलेक्ट्रॉनिक) देता है।',
            },
            {
              english: 'Other boranes: nido- and arachno- families. Boron carbide $B_4C$ — extremely hard.',
        hindi: 'अन्य बोरेन: निडो- और एराचनो- परिवार। बोरोन कार्बाइड $B_4C$ - अत्यंत कठोर।',
            },
          ],
        },
        {
          type: 'figure',
          caption: {
            english: 'Bridge bonding in $B_2H_6$: two B atoms held by two 3-centre, 2-electron B–H–B bridges; 4 terminal B–H bonds are normal 2-centre bonds.',
        hindi: '$B_2H_6$ में ब्रिज बॉन्डिंग: दो B परमाणु दो 3-केंद्र, 2-इलेक्ट्रॉन B-H-B ब्रिज द्वारा धारण किए जाते हैं; 4 टर्मिनल बी-एच बांड सामान्य 2-केंद्र बांड हैं।',
          },
          svg: `<svg viewBox="0 0 360 160" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Diborane B2H6 banana-bond structure">
            <text x="100" y="90" font-size="22" text-anchor="middle">B</text>
            <text x="260" y="90" font-size="22" text-anchor="middle">B</text>
            <text x="180" y="50" font-size="18" text-anchor="middle">H</text>
            <text x="180" y="130" font-size="18" text-anchor="middle">H</text>
            <text x="60"  y="60" font-size="18" text-anchor="middle">H</text>
            <text x="60"  y="130" font-size="18" text-anchor="middle">H</text>
            <text x="300" y="60" font-size="18" text-anchor="middle">H</text>
            <text x="300" y="130" font-size="18" text-anchor="middle">H</text>
            <line x1="105" y1="85" x2="170" y2="55" stroke="#333"/>
            <line x1="190" y1="55" x2="255" y2="85" stroke="#333"/>
            <line x1="105" y1="95" x2="170" y2="125" stroke="#333"/>
            <line x1="190" y1="125" x2="255" y2="95" stroke="#333"/>
            <line x1="92" y1="80" x2="68" y2="65" stroke="#333"/>
            <line x1="92" y1="100" x2="68" y2="125" stroke="#333"/>
            <line x1="268" y1="80" x2="292" y2="65" stroke="#333"/>
            <line x1="268" y1="100" x2="292" y2="125" stroke="#333"/>
          </svg>`,
        },
      ],
    },
    {
      id: 'aluminium',
      title: {
        english: 'Aluminium & Its Compounds',
        hindi: 'एल्युमिनियम और उसके यौगिक',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Most abundant **metal** in the Earth\'s crust (~8.3 %); ores: bauxite $Al_2O_3\\cdot 2H_2O$, cryolite $Na_3AlF_6$, corundum $Al_2O_3$.',
        hindi: 'पृथ्वी की पपड़ी में सबसे प्रचुर **धातु** (~8.3%); अयस्क: बॉक्साइट $Al_2O_3\\cdot 2H_2O$, क्रायोलाइट $Na_3AlF_6$, कोरन्डम $Al_2O_3$।',
            },
            {
              english: 'Extraction: **Bayer process** purifies bauxite → pure alumina; **Hall–Héroult process** electrolyses molten $Al_2O_3$ in cryolite + fluorite at ~1270 K with carbon electrodes.',
        hindi: 'निष्कर्षण: **बायर प्रक्रिया** बॉक्साइट को शुद्ध करती है → शुद्ध एल्यूमिना; **हॉल-हेरोल्ट प्रक्रिया** कार्बन इलेक्ट्रोड के साथ ~1270 K पर क्रायोलाइट + फ्लोराइट में पिघले हुए $Al_2O_3$ को इलेक्ट्रोलाइज करता है।',
            },
            {
              english: 'Uses: aircraft, packaging foil, electrical conductors, alloys (duralumin, magnalium).',
        hindi: 'उपयोग: विमान, पैकेजिंग फ़ॉइल, विद्युत कंडक्टर, मिश्र धातु (ड्यूरालुमिन, मैग्नेलियम)।',
            },
            {
              english: '**$AlCl_3$**: covalent in vapour (dimer $Al_2Cl_6$ with bridging Cl); ionic in aqueous solution. Strong Lewis acid; catalyst in Friedel-Crafts.',
        hindi: '**$AlCl_3$**: वाष्प में सहसंयोजक (डिमर $Al_2Cl_6$ ब्रिजिंग सीएल के साथ); जलीय घोल में आयनिक. मजबूत लुईस एसिड; फ्रीडेल-क्राफ्ट्स में उत्प्रेरक।',
            },
            {
              english: '**Alums** $M^I M^{III}(SO_4)_2 \\cdot 12 H_2O$ — e.g. potash alum $KAl(SO_4)_2\\cdot 12H_2O$. Used for water purification (coagulation), tanning leather, mordant in dyeing.',
        hindi: '**फिटकरी** $M^I M^{III}(SO_4)_2 \\cdot 12 H_2O$ — जैसे। पोटाश फिटकरी $KAl(SO_4)_2\\cdot 12H_2O$. जल शोधन (जमावट), चमड़े को कमाना, रंगाई में मार्डेंट के लिए उपयोग किया जाता है।',
            },
            {
              english: '**Thermite reaction**: $2Al + Fe_2O_3 \\to Al_2O_3 + 2Fe$ (highly exothermic, used to weld broken rails).',
        hindi: '**थर्माइट प्रतिक्रिया**: $2Al + Fe_2O_3 \\to Al_2O_3 + 2Fe$ (अत्यधिक ऊष्माक्षेपी, टूटी रेल को वेल्ड करने के लिए उपयोग किया जाता है)।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: 'BX_3:\\ BF_3 < BCl_3 < BBr_3 < BI_3\\ \\text{(Lewis acidity)}',
      desc: {
        english: 'Back-bonding decreases up the order',
        hindi: 'बैक-बॉन्डिंग क्रम में घटती जाती है',
      },
    },
    {
      latex: 'B(OH)_3 + H_2O \\rightleftharpoons [B(OH)_4]^- + H^+',
      desc: {
        english: '$H_3BO_3$ is monobasic Lewis acid',
        hindi: '$H_3BO_3$ मोनोबैसिक लुईस एसिड है',
      },
    },
    {
      latex: '4BF_3 + 3LiAlH_4 \\to 2B_2H_6 + 3LiF + 3AlF_3',
      desc: {
        english: 'Diborane synthesis',
        hindi: 'डिबोरेन संश्लेषण',
      },
    },
    {
      latex: 'B_2H_6 + 6NH_3 \\xrightarrow{\\Delta} 2B_3N_3H_6 + 12H_2',
      desc: {
        english: 'Borazine (inorganic benzene)',
        hindi: 'बोराज़िन (अकार्बनिक बेंजीन)',
      },
    },
    {
      latex: '2Al + 2NaOH + 6H_2O \\to 2Na[Al(OH)_4] + 3H_2',
      desc: {
        english: 'Amphoteric Al',
        hindi: 'एम्फोटेरिक अल',
      },
    },
    {
      latex: '2Al + Fe_2O_3 \\to Al_2O_3 + 2Fe',
      desc: {
        english: 'Thermite',
        hindi: 'थर्माइट',
      },
    },
    {
      latex: 'Al_2Cl_6\\ \\text{dimer with bridging Cl}',
      desc: {
        english: 'Vapour-phase $AlCl_3$',
        hindi: 'वाष्प-चरण $AlCl_3$',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Lewis acid',
      problem: {
        english: 'Correct order of Lewis acidity of boron tri-halides:',
        hindi: 'बोरॉन ट्राई-हैलाइड्स की लुईस अम्लता का सही क्रम:',
      },
      answer: {
        english: '$BF_3 < BCl_3 < BBr_3 < BI_3$',
        hindi: '$BF_3 < BCl_3 < BBr_3 < BI_3$',
      },
      solution: {
        english: 'p$\\pi$–p$\\pi$ back-bonding strongest with F.',
        hindi: 'p$\\pi$–p$\\pi$ F के साथ बैक-बॉन्डिंग सबसे मजबूत है।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Diborane',
      problem: {
        english: 'Number of 3-centre 2-electron bonds in diborane:',
        hindi: 'डाइबोरेन में 3-केंद्र 2-इलेक्ट्रॉन बांड की संख्या:',
      },
      answer: {
        english: '2',
        hindi: '2',
      },
      solution: {
        english: 'Two bridging B–H–B bonds.',
        hindi: 'दो ब्रिजिंग बी-एच-बी बांड।',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Borax',
      problem: {
        english: 'Borax bead with $CuSO_4$ in oxidising flame is:',
        hindi: 'ऑक्सीकरण ज्वाला में $CuSO_4$ के साथ बोरेक्स मनका है:',
      },
      answer: {
        english: 'Blue',
        hindi: 'नीला',
      },
      solution: {
        english: 'Forms cupric metaborate $Cu(BO_2)_2$.',
        hindi: 'क्यूप्रिक मेटाबोरेट $Cu(BO_2)_2$ बनाता है।',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Inert-pair',
      problem: {
        english: 'Most stable +1 oxidation state in Gp 13 is shown by:',
        hindi: 'जीपी 13 में सबसे स्थिर +1 ऑक्सीकरण अवस्था निम्न द्वारा दर्शाई गई है:',
      },
      answer: {
        english: 'Tl',
        hindi: 'टीएल',
      },
      solution: {
        english: 'Inert-pair effect maximum at the bottom.',
        hindi: 'नीचे की ओर जड़-युग्म प्रभाव अधिकतम।',
      },
    },
    {
      year: 2020,
      exam: 'JEE Main',
      tag: 'Boric acid',
      problem: {
        english: '$H_3BO_3$ acts as which kind of acid?',
        hindi: '$H_3BO_3$ किस प्रकार के अम्ल के रूप में कार्य करता है?',
      },
      answer: {
        english: 'Monobasic Lewis acid',
        hindi: 'मोनोबैसिक लुईस एसिड',
      },
      solution: {
        english: 'Accepts OH⁻ from water rather than donating H⁺.',
        hindi: 'H⁺ दान करने के बजाय पानी से OH⁻ स्वीकार करता है।',
      },
    },
  ],
  next: {
    slug: 'p-block-14',
    title: 'p-Block Group 14 — Carbon Family',
  },
};

export default chapter;
